/**
 * PulseTile — Single metric tile for the dashboard Pulse Row
 *
 * Shows a label (uppercase), a large number (with count-up animation),
 * and an optional delta indicator. Tapping navigates to the relevant list page.
 */

import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useReducedMotion } from "framer-motion";
import { TrendingUp, TrendingDown, Minus } from "lucide-react";
import { cn } from "@/lib/utils";
import { CARD } from "@/config/design-tokens";
import { PulseSparkline } from "./PulseSparkline";
import type { PulsePeriod, SparklinePoint } from "@/hooks/usePulseSparkline";

const PERIOD_LABELS: Record<string, string> = {
  "7d": "7 days",
  "30d": "30 days",
  "90d": "90 days",
};

interface PulseTileProps {
  label: string;
  value: number;
  delta?: number | null;
  href: string;
  isLoading?: boolean;
  isZero?: boolean;
  sparklineData?: SparklinePoint[];
  isSparklineLoading?: boolean;
  /** Metric type for ghost sparkline color differentiation */
  sparklineMetric?: "orders" | "revenue" | "products";
  /** Period comparison from sparkline summary */
  periodDelta?: number | null;
  periodDeltaPct?: number | null;
  periodAvg?: number | null;
  period?: PulsePeriod;
  /** Muted caption clarifying the value's timeframe, e.g. "so far today". */
  timeframeNote?: string;
}

function useCountUp(target: number, duration = 400): number {
  const reducedMotion = useReducedMotion();
  const [current, setCurrent] = useState(0);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    if (reducedMotion || target === 0) {
      setCurrent(target);
      return;
    }

    setCurrent(0);
    const start = performance.now();

    function animate(now: number) {
      const elapsed = now - start;
      const progress = Math.min(elapsed / duration, 1);
      // ease-out cubic
      const eased = 1 - Math.pow(1 - progress, 3);
      setCurrent(Math.round(eased * target));

      if (progress < 1) {
        rafRef.current = requestAnimationFrame(animate);
      }
    }

    rafRef.current = requestAnimationFrame(animate);

    return () => {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, [target, duration, reducedMotion]);

  return current;
}

function formatValue(value: number): string {
  if (value >= 1_000_000) {
    return new Intl.NumberFormat("en", {
      notation: "compact",
      maximumFractionDigits: 1,
    }).format(value);
  }
  return new Intl.NumberFormat("en").format(value);
}

function PeriodStats({
  periodDelta,
  periodDeltaPct,
  periodAvg,
  period,
  isZero,
}: {
  periodDelta?: number | null;
  periodDeltaPct?: number | null;
  periodAvg?: number | null;
  period: string;
  isZero?: boolean;
}) {
  const periodLabel = PERIOD_LABELS[period] ?? period;

  if (isZero) {
    return (
      <div className="mt-1 space-y-0.5">
        <div className="flex items-center gap-1 text-[10px] text-muted-foreground/50">
          <Minus className="w-2.5 h-2.5" />
          <span>vs last {periodLabel}</span>
        </div>
        <p className="text-[10px] text-muted-foreground/50">~0/day</p>
      </div>
    );
  }

  const hasDelta = periodDelta != null && periodDelta !== 0;
  const isUp = (periodDelta ?? 0) > 0;

  return (
    <div className="mt-1 space-y-0.5">
      {hasDelta ? (
        <div
          className={cn(
            "flex items-center gap-1 text-[10px] font-medium",
            isUp ? "text-green-600 dark:text-green-400" : "text-red-500 dark:text-red-400"
          )}
        >
          {isUp ? <TrendingUp className="w-2.5 h-2.5" /> : <TrendingDown className="w-2.5 h-2.5" />}
          <span>
            {periodDeltaPct != null ? `${isUp ? "+" : ""}${periodDeltaPct}%` : `${isUp ? "+" : ""}${periodDelta}`}
            {" "}vs last {periodLabel}
          </span>
        </div>
      ) : (
        <div className="flex items-center gap-1 text-[10px] text-muted-foreground">
          <Minus className="w-2.5 h-2.5" />
          <span>0% vs last {periodLabel}</span>
        </div>
      )}
      {periodAvg != null && (
        <p className="text-[10px] text-muted-foreground">
          ~{formatValue(Math.round(periodAvg))}/day
        </p>
      )}
    </div>
  );
}

export function PulseTile({ label, value, delta, href, isLoading, isZero, sparklineData, isSparklineLoading, sparklineMetric, periodDelta, periodDeltaPct, periodAvg, period, timeframeNote }: PulseTileProps) {
  const reducedMotion = useReducedMotion();
  const animatedValue = useCountUp(isLoading ? 0 : value);

  if (isLoading) {
    return (
      <div
        className={cn(
          CARD, "flex-1 min-w-0 p-4"
        )}
        aria-hidden="true"
        role="presentation"
      >
        <div className="flex flex-col justify-between min-h-[120px]">
          <div className="h-3 w-16 bg-muted rounded-md animate-pulse" />
          <div>
            <div className="h-7 w-12 bg-muted rounded-md animate-pulse mb-2" />
            <div className="h-3 w-24 bg-muted rounded-md animate-pulse" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <Link
      to={href}
      className={cn(
        CARD, "shadow-none",
        "relative flex-1 min-w-0 p-4",
        "transition-all duration-200",
        "hover:scale-[1.02] hover:shadow-apple-sm active:scale-[0.98]"
      )}
      aria-label={`${value} ${label}`}
    >
      <div className="relative z-10 flex flex-col min-h-[160px]">
        <p className="text-xs font-medium uppercase tracking-wider truncate text-muted-foreground">
          {label}
        </p>
        {timeframeNote && (
          <p className="text-[10px] font-normal text-muted-foreground/60 truncate mt-0.5">
            {timeframeNote}
          </p>
        )}
        <p className={cn(
          "text-3xl font-semibold leading-tight mt-1.5",
          isZero ? "text-muted-foreground" : "text-foreground"
        )}>
          {formatValue(animatedValue)}
        </p>
        <div className="mt-1">
          {/* Period comparison — shown only for tiles with sparkline metrics */}
          {period != null && sparklineMetric != null ? (
            <PeriodStats
              periodDelta={periodDelta}
              periodDeltaPct={periodDeltaPct}
              periodAvg={periodAvg}
              period={period}
              isZero={isZero}
            />
          ) : (
            /* Fallback: original "from yesterday" delta for non-sparkline tiles */
            delta != null && delta !== 0 && (
              <div
                className={cn(
                  "flex items-center gap-1 mt-1 text-xs font-medium",
                  delta > 0
                    ? "text-green-600 dark:text-green-400"
                    : "text-red-500 dark:text-red-400"
                )}
              >
                {delta > 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                <span>{delta > 0 ? "+" : ""}{delta} from yesterday</span>
              </div>
            )
          )}
        </div>
      </div>
      {!isSparklineLoading && ((sparklineData && sparklineData.length > 0) || isZero) && (
        <div className={cn("absolute bottom-0 left-0 right-0 h-[55%]", !reducedMotion && "transition-opacity duration-200")}>
          <PulseSparkline data={sparklineData ?? []} isZero={isZero} metric={sparklineMetric} />
        </div>
      )}
    </Link>
  );
}
